import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ApplicationCard = ({ application }) => {
  const navigate = useNavigate();

  const statusConfig = {
    draft: {
      color: 'bg-muted text-muted-foreground',
      icon: 'FileEdit',
      label: 'Draft',
    },
    submitted: {
      color: 'bg-accent/20 text-accent-foreground',
      icon: 'Clock',
      label: 'Under Review',
    },
    accepted: {
      color: 'bg-success/20 text-success-foreground',
      icon: 'CheckCircle',
      label: 'Accepted',
    },
    rejected: {
      color: 'bg-destructive/20 text-destructive-foreground',
      icon: 'XCircle',
      label: 'Rejected',
    },
    pending: {
      color: 'bg-warning/20 text-warning-foreground',
      icon: 'AlertCircle',
      label: 'Action Required',
    },
  };

  const currentStatus = statusConfig?.[application?.status] || statusConfig?.draft;

  const handleContinue = () => {
    if (application?.status === 'draft') {
      navigate('/application-form', { state: { applicationId: application?.id } });
    } else {
      navigate('/institution-profile', { state: { institutionId: application?.institutionId } });
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm-sm hover:shadow-warm transition-smooth overflow-hidden">
      <div className="flex flex-col lg:flex-row">
        <div className="w-full lg:w-48 h-48 lg:h-auto flex-shrink-0 overflow-hidden">
          <Image
            src={application?.institutionImage}
            alt={application?.institutionImageAlt}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="flex-1 p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
            <div className="flex-1">
              <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
                {application?.institutionName}
              </h3>
              <p className="text-sm md:text-base text-muted-foreground font-caption mb-2">
                {application?.programType}
              </p>
              <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                <Icon name="MapPin" size={16} color="var(--color-muted-foreground)" />
                <span className="font-caption">{application?.location}</span>
              </div>
            </div>

            <div className={`${currentStatus?.color} px-3 py-1.5 rounded-md flex items-center gap-2 self-start`}>
              <Icon name={currentStatus?.icon} size={16} />
              <span className="text-xs md:text-sm font-medium font-caption whitespace-nowrap">
                {currentStatus?.label}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Icon name="Calendar" size={18} color="var(--color-primary)" />
              <div>
                <p className="text-xs text-muted-foreground font-caption">Submitted</p>
                <p className="text-sm font-medium text-foreground data-text">
                  {application?.submittedDate}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Icon name="Clock" size={18} color="var(--color-primary)" />
              <div>
                <p className="text-xs text-muted-foreground font-caption">Deadline</p>
                <p className="text-sm font-medium text-foreground data-text">
                  {application?.deadline}
                </p>
              </div>
            </div>
          </div>

          {application?.nextAction && (
            <div className="bg-accent/10 border border-accent/20 rounded-md p-3 mb-4">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={18} color="var(--color-accent)" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">Next Action</p>
                  <p className="text-xs md:text-sm text-muted-foreground font-caption">
                    {application?.nextAction}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
            <Button
              variant={application?.status === 'draft' ? 'default' : 'outline'}
              iconName={application?.status === 'draft' ? 'Edit' : 'Eye'}
              iconPosition="left"
              onClick={handleContinue}
              className="flex-1 sm:flex-initial"
            >
              {application?.status === 'draft' ? 'Continue Application' : 'View Details'}
            </Button>

            {application?.status === 'accepted' && (
              <Button
                variant="success"
                iconName="Download"
                iconPosition="left"
                className="flex-1 sm:flex-initial"
              >
                Download Letter
              </Button>
            )}

            {application?.status === 'draft' && (
              <Button
                variant="ghost"
                iconName="Trash2"
                iconPosition="left"
                className="flex-1 sm:flex-initial"
              >
                Delete Draft
              </Button>
            )}
          </div>
        </div>
      </div>
      {application?.progress && application?.status === 'draft' && (
        <div className="px-4 md:px-6 pb-4">
          <div className="flex items-center justify-between text-xs md:text-sm mb-2">
            <span className="text-muted-foreground font-caption">Application Progress</span>
            <span className="font-medium text-foreground data-text">{application?.progress}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-smooth"
              style={{ width: `${application?.progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationCard;